/**
 * Triangle channel wrapper.
 *
 * Loads the triangle AudioWorklet module and exposes a thin API for
 * note on/off + frequency control. NES triangle has no volume control.
 */

let workletLoaded = false;

/** Idempotently load the triangle worklet into the given AudioContext. */
export async function loadTriangleWorklet(ctx: AudioContext): Promise<void> {
  if (workletLoaded) return;
  await ctx.audioWorklet.addModule('/worklets/triangle.js');
  workletLoaded = true;
}

export interface TriangleNode {
  setFrequency(hz: number): void;
  /** `attackMs > 0` adds a re-attack click so back-to-back notes read as distinct. */
  noteOn(attackMs?: number): void;
  noteOff(): void;
  destroy(): void;
}

/**
 * Create a TriangleNode wired to ctx.destination.
 * The triangle worklet module must already be loaded — call
 * loadTriangleWorklet(ctx) first.
 */
export function createTriangleNode(ctx: AudioContext): TriangleNode {
  const node = new AudioWorkletNode(ctx, 'triangle-processor', {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [1],
  });
  node.connect(ctx.destination);

  return {
    setFrequency(hz: number) {
      const p = node.parameters.get('frequency');
      if (p) p.value = hz;
    },
    noteOn: (attackMs = 0) => node.port.postMessage({ type: 'noteOn', attackMs }),
    noteOff: () => node.port.postMessage({ type: 'noteOff' }),
    destroy: () => {
      node.port.postMessage({ type: 'noteOff' });
      node.disconnect();
    },
  };
}
